import React from "react";
import { useTranslation } from "react-i18next";
import { X, SendHorizontal, RefreshCcw, ShieldCheck } from "lucide-react";
import { streamChat, newSessionId } from "@/lib/chat";

// State-based 3D character poses. Same character across all four images.
const POSES = {
    idle: "/dr-idle.png",
    wave: "/dr-wave.png",
    thinking: "/dr-thinking.png",
    talking: "/dr-talking.png",
};

const GREETING_DISMISS_KEY = "drk-greeting-dismissed";

// -----------------------------------------------------------------
// Timing constants (all in milliseconds)
// -----------------------------------------------------------------
const GREETING_APPEAR_DELAY_MS = 1500;   // pause before greeting bubble pops
const GREETING_AUTO_HIDE_MS = 8000;      // greeting auto-dismiss timer
const WAVE_DURATION_MS = 900;            // wave animation on first open
const IDLE_TIMEOUT_MS = 800;             // debounce between talking/idle
const THINKING_MIN_MS = 2200;            // minimum "thinking" pose duration
const AVATAR_BLINK_INTERVAL_MS = 1600;   // blink cadence for idle avatar

/**
 * CharacterAvatar — cross-fades between the 4 pose PNGs.
 * Adds subtle life-like motion (blink flash, sway, mouth-flap during talking) via CSS.
 */
function CharacterAvatar({ pose, size = "lg", ringed = false, animate = true }) {
    // Preload all poses once so state changes are instant + no flicker
    React.useEffect(() => {
        Object.values(POSES).forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    // Rapid mouth-flap during "talking": alternate between talking + idle every ~140ms
    const [talkingFrame, setTalkingFrame] = React.useState("talking");
    React.useEffect(() => {
        if (pose !== "talking" || !animate) {
            setTalkingFrame("talking");
            return;
        }
        let alive = true;
        let flip = false;
        const tick = () => {
            if (!alive) return;
            flip = !flip;
            setTalkingFrame(flip ? "idle" : "talking");
        };
        const id = setInterval(tick, 150 + Math.random() * 90);
        return () => {
            alive = false;
            clearInterval(id);
        };
    }, [pose, animate]);

    const sizeMap = {
        sm: "h-7 w-7",
        md: "h-11 w-11",
        lg: "h-16 w-16 sm:h-[68px] sm:w-[68px]",
    };

    // Effective pose for the "rendered top image"
    const effectivePose = pose === "talking" ? talkingFrame : pose;

    return (
        <span
            className={`relative inline-flex ${sizeMap[size]} items-center justify-center rounded-full overflow-hidden bg-white ${ringed ? "border-[3px] border-white" : "border border-white"}`}
            style={
                ringed
                    ? { boxShadow: "0 18px 40px rgba(15,39,71,0.25), 0 0 0 2px rgba(14,124,134,0.35)" }
                    : { boxShadow: "0 4px 10px rgba(15,39,71,0.15)" }
            }
            aria-hidden
        >
            <span
                className="relative block h-full w-full"
                style={{
                    animation: animate
                        ? "chatBreath 4.6s ease-in-out infinite, chatSway 6.4s ease-in-out infinite, chatBlink 5.8s ease-in-out infinite"
                        : "none",
                    transformOrigin: "50% 60%",
                }}
            >
                {Object.entries(POSES).map(([key, src]) => (
                    <img
                        key={key}
                        src={src}
                        alt=""
                        draggable={false}
                        className="absolute inset-0 h-full w-full object-cover object-top"
                        style={{
                            opacity: effectivePose === key ? 1 : 0,
                            // Slight head-tilts based on pose
                            transform:
                                effectivePose === key && key === "wave"
                                    ? "rotate(-2deg)"
                                    : effectivePose === key && key === "thinking"
                                      ? "rotate(1.5deg)"
                                      : "rotate(0deg)",
                            // Fast transitions during talking mouth-flap, softer otherwise
                            transition:
                                pose === "talking"
                                    ? "opacity 90ms linear, transform 200ms ease-out"
                                    : "opacity 500ms ease-out, transform 500ms ease-out",
                        }}
                    />
                ))}
            </span>
        </span>
    );
}

/** Greeting speech bubble that pops above the floating avatar on first visit. */
function GreetingBubble({ show, onOpen, onClose }) {
    const { t } = useTranslation();
    if (!show) return null;
    return (
        <div
            data-testid="chat-greeting-bubble"
            className="absolute right-0 z-[61] w-[260px] sm:w-[290px]"
            style={{
                bottom: "calc(100% + 14px)",
                animation: "greetingIn 420ms cubic-bezier(0.2, 0.9, 0.3, 1.3) both",
            }}
        >
            <div
                className="relative rounded-2xl bg-white shadow-[0_18px_44px_rgba(15,39,71,0.18)] border border-[color:var(--border-default)] pl-3 pr-8 py-3"
                role="dialog"
                aria-label={t("chat.greetingBubbleLabel")}
            >
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    aria-label={t("chat.dismissGreeting")}
                    data-testid="chat-greeting-close"
                    className="absolute top-1.5 right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-soft)] hover:bg-[color:var(--bg-subtle)]"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
                <p className="text-[13px] leading-snug text-[color:var(--text-primary)]">
                    <span className="font-display font-semibold text-[color:var(--brand-navy)]">
                        {t("chat.greetingBubbleLabel")}
                    </span>
                    <br />
                    {t("chat.greetingBubble")}
                </p>
                <button
                    type="button"
                    onClick={onOpen}
                    className="mt-2 inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-teal)] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[color:var(--brand-teal-2)]"
                    data-testid="chat-greeting-open"
                >
                    {t("chat.startChat")}
                </button>
                {/* Tail pointing to avatar (bottom-right) */}
                <span
                    aria-hidden
                    className="absolute -bottom-[7px] right-8 block h-4 w-4 rotate-45 bg-white border-r border-b border-[color:var(--border-default)]"
                />
            </div>
        </div>
    );
}

export function DoctorChatAssistant() {
    const { t, i18n } = useTranslation();
    const currentLang = (i18n.resolvedLanguage || i18n.language || "en")
        .toLowerCase()
        .startsWith("fr")
        ? "fr"
        : "en";
    const SUGGESTIONS = [
        t("chat.suggestion1"),
        t("chat.suggestion2"),
        t("chat.suggestion3"),
        t("chat.suggestion4"),
        t("chat.suggestion5"),
    ];
    const INTRO = {
        role: "assistant",
        content: t("chat.intro"),
    };
    const [open, setOpen] = React.useState(false);
    const [messages, setMessages] = React.useState([INTRO]);
    const [input, setInput] = React.useState("");
    const [isStreaming, setIsStreaming] = React.useState(false);
    const [lastUserMessage, setLastUserMessage] = React.useState("");
    const [pose, setPose] = React.useState("idle"); // 'idle' | 'wave' | 'thinking' | 'talking'
    const [error, setError] = React.useState("");
    const [greetingShown, setGreetingShown] = React.useState(false);
    const [sessionId, setSessionId] = React.useState(() => newSessionId()); // reset on page refresh
    const abortRef = React.useRef(null);
    const scrollRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const waveTimerRef = React.useRef(null);
    const idleTimerRef = React.useRef(null);
    const glanceTimerRef = React.useRef(null);
    const panelId = "dr-kichian-chat-panel";

    // When the site language changes, drop any in-flight stream, refresh
    // the intro bubble in the new language, and start a fresh AI session so
    // the model isn't holding half-EN/half-FR context.
    const prevLangRef = React.useRef(currentLang);
    React.useEffect(() => {
        if (prevLangRef.current !== currentLang) {
            prevLangRef.current = currentLang;
            if (abortRef.current) abortRef.current.abort();
            setMessages([{ role: "assistant", content: t("chat.intro") }]);
            setInput("");
            setError("");
            setIsStreaming(false);
            setLastUserMessage("");
            setSessionId(newSessionId());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLang]);

    // Auto-scroll to bottom on new content
    React.useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, isStreaming, pose]);

    // Focus input when opening + play a friendly wave animation
    React.useEffect(() => {
        if (open) {
            const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 120);
            playWave();
            return () => clearTimeout(t);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // ESC closes
    React.useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape") setOpen(false);
        }
        if (open) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // Show the greeting bubble on first visit (session-only, dismissable).
    // NOTE: sessionStorage is used ONLY to store a boolean "greeting was
    // dismissed" flag under GREETING_DISMISS_KEY — no PHI, no user content,
    // no sensitive data. It's the standard mechanism for a per-session UI
    // preference and cleared automatically when the tab closes.
    React.useEffect(() => {
        try {
            if (sessionStorage.getItem(GREETING_DISMISS_KEY)) return;
        } catch (storageErr) {
            // Private-browsing / sandbox / quota — treat as "not dismissed"
            // so we still show the greeting, and just log for diagnostics.
            if (typeof console !== "undefined") {
                console.debug("greeting: sessionStorage read blocked", storageErr);
            }
        }
        const t = setTimeout(() => setGreetingShown(true), GREETING_APPEAR_DELAY_MS);
        return () => clearTimeout(t);
    }, []);

    // Human-like "idle glance" — occasionally flick to a subtle pose while idle & closed
    React.useEffect(() => {
        function scheduleNextGlance() {
            const wait = 8000 + Math.random() * 8000; // every 8–16s
            glanceTimerRef.current = setTimeout(() => {
                if (pose === "idle" && !isStreaming) {
                    const glances = ["thinking", "wave"];
                    const g = glances[Math.floor(Math.random() * glances.length)];
                    setPose(g);
                    setTimeout(() => {
                        setPose((p) => (p === g ? "idle" : p));
                        scheduleNextGlance();
                    }, 900 + Math.random() * 800);
                } else {
                    scheduleNextGlance();
                }
            }, wait);
        }
        scheduleNextGlance();
        return () => {
            if (glanceTimerRef.current) clearTimeout(glanceTimerRef.current);
        };
    }, [pose, isStreaming]);

    // Cleanup any in-flight stream / timers on unmount (page navigation)
    React.useEffect(() => {
        return () => {
            if (abortRef.current) abortRef.current.abort();
            if (waveTimerRef.current) clearTimeout(waveTimerRef.current);
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (glanceTimerRef.current) clearTimeout(glanceTimerRef.current);
        };
    }, []);

    function playWave(duration = 2200) {
        setPose("wave");
        if (waveTimerRef.current) clearTimeout(waveTimerRef.current);
        waveTimerRef.current = setTimeout(() => {
            setPose((p) => (p === "wave" ? "idle" : p));
        }, duration);
    }

    function dismissGreeting() {
        setGreetingShown(false);
        try {
            // Store only a boolean flag — no PHI or sensitive data.
            sessionStorage.setItem(GREETING_DISMISS_KEY, "1");
        } catch (storageErr) {
            // Storage may be blocked (private mode / quota). Not fatal —
            // the greeting just may reappear next visit. Log for diagnostics.
            if (typeof console !== "undefined") {
                console.debug("greeting: sessionStorage write blocked", storageErr);
            }
        }
    }

    function handleOpenFromGreeting() {
        dismissGreeting();
        setOpen(true);
    }

    function handleAvatarClick() {
        dismissGreeting();
        setOpen((v) => !v);
    }

    function handleReset() {
        if (abortRef.current) abortRef.current.abort();
        setMessages([INTRO]);
        setInput("");
        setError("");
        setIsStreaming(false);
        playWave();
    }

    async function sendMessage(userText) {
        const trimmed = (userText ?? input).trim();
        if (!trimmed || isStreaming) return;
        setError("");
        setLastUserMessage(trimmed);
        const nextMessages = [...messages, { role: "user", content: trimmed }];
        setMessages([...nextMessages, { role: "assistant", content: "" }]);
        setInput("");
        setIsStreaming(true);
        setPose("thinking");

        const controller = new AbortController();
        abortRef.current = controller;

        let receivedAny = false;
        await streamChat(
            {
                session_id: sessionId,
                lang: currentLang,
                messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
            },
            {
                onDelta: (delta) => {
                    if (!receivedAny) {
                        receivedAny = true;
                        setPose("talking");
                    }
                    setMessages((prev) => {
                        const copy = prev.slice();
                        const last = copy[copy.length - 1];
                        if (last && last.role === "assistant") {
                            copy[copy.length - 1] = { ...last, content: last.content + delta };
                        }
                        return copy;
                    });
                },
                onDone: () => {
                    setIsStreaming(false);
                    abortRef.current = null;
                    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
                    idleTimerRef.current = setTimeout(() => setPose("idle"), 900);
                },
                onError: (err) => {
                    setIsStreaming(false);
                    abortRef.current = null;
                    setPose("idle");
                    setError(err.message || t("chat.errorFallback"));
                    setMessages((prev) => {
                        const copy = prev.slice();
                        const last = copy[copy.length - 1];
                        if (last && last.role === "assistant" && !last.content) copy.pop();
                        return copy;
                    });
                },
                signal: controller.signal,
            },
        );
    }

    function onKeyDownInput(e) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    }

    const statusText = isStreaming
        ? pose === "thinking"
            ? t("chat.statusThinking")
            : t("chat.statusExplaining")
        : t("chat.statusIdle");

    return (
        <>
            {/* Floating avatar trigger (with greeting bubble positioned relative to it) */}
            <div className="fixed bottom-5 right-5 z-[60]" data-testid="chat-avatar-container">
                <GreetingBubble
                    show={greetingShown && !open}
                    onOpen={handleOpenFromGreeting}
                    onClose={dismissGreeting}
                />
                <button
                    type="button"
                    aria-label={open ? t("chat.closeChatAria") : t("chat.openChat")}
                    aria-controls={panelId}
                    aria-expanded={open}
                    onClick={handleAvatarClick}
                    data-testid="chat-avatar-trigger"
                    className="relative group focus:outline-none"
                >
                    <span className="sr-only">
                        {open ? t("chat.closeChatAria") : t("chat.openChat")}
                    </span>
                    {/* Halo pulses */}
                    <span
                        aria-hidden
                        className="absolute inset-0 rounded-full opacity-70"
                        style={{
                            background:
                                "radial-gradient(closest-side, rgba(14,124,134,0.32) 0%, rgba(14,124,134,0) 70%)",
                            animation: "chatHalo 2.6s ease-out infinite",
                        }}
                    />
                    <span
                        aria-hidden
                        className="absolute inset-0 rounded-full"
                        style={{
                            boxShadow: "0 0 0 0 rgba(14,124,134,0.55)",
                            animation: "chatPulseRing 2.6s ease-out infinite",
                        }}
                    />
                    {/* The animated character */}
                    <span
                        className="relative inline-flex transition-transform duration-200 group-hover:scale-105 group-active:scale-95"
                        style={{ animation: "chatBob 4.2s ease-in-out infinite" }}
                        onMouseEnter={() => {
                            if (!open && !isStreaming) playWave(1600);
                        }}
                    >
                        <CharacterAvatar pose={open ? pose : pose === "wave" ? "wave" : "idle"} size="lg" ringed />
                    </span>
                    {/* Presence dot */}
                    <span
                        aria-hidden
                        className="absolute bottom-1 right-1 h-3.5 w-3.5 rounded-full border-2 border-white"
                        style={{
                            background: "#12b981",
                            boxShadow: "0 0 0 2px rgba(18,185,129,0.35)",
                        }}
                    />
                </button>
            </div>

            {/* Chat panel */}
            {open && (
                <div
                    id={panelId}
                    role="dialog"
                    aria-modal="false"
                    aria-label={t("chat.assistantName")}
                    data-testid="chat-panel"
                    className="fixed z-[60] bottom-24 right-2 sm:right-5 w-[96vw] sm:w-[420px] max-w-[440px] card-soft flex flex-col overflow-hidden"
                    style={{
                        height: "min(600px, 82vh)",
                        animation: "chatPanelIn 240ms ease-out both",
                    }}
                >
                    {/* Header with animated avatar */}
                    <div
                        className="flex items-start gap-3 p-4 border-b border-[color:var(--border-default)]"
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(14,124,134,0.14) 0%, rgba(255,255,255,1) 60%)",
                        }}
                    >
                        <span data-testid={`chat-header-pose-${pose}`}>
                            <CharacterAvatar pose={pose} size="md" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-base font-semibold text-[color:var(--brand-navy)] leading-tight">
                                {t("chat.assistantName")}
                            </p>
                            <p className="text-[11px] uppercase tracking-wider text-[color:var(--brand-teal-2)] mt-0.5 flex items-center gap-1.5">
                                <span
                                    className={`inline-block h-1.5 w-1.5 rounded-full ${isStreaming ? "bg-amber-500" : "bg-emerald-500"}`}
                                    style={{
                                        animation: isStreaming ? "statusBlink 1s ease-in-out infinite" : "none",
                                    }}
                                />
                                <span data-testid="chat-status">{statusText}</span>
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleReset}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[color:var(--text-soft)] hover:bg-[color:var(--bg-subtle)]"
                            title={t("chat.resetTitle")}
                            aria-label={t("chat.resetTitle")}
                            data-testid="chat-reset"
                        >
                            <RefreshCcw className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[color:var(--text-soft)] hover:bg-[color:var(--bg-subtle)]"
                            title={t("chat.close")}
                            aria-label={t("chat.closeChat")}
                            data-testid="chat-close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[color:var(--bg-canvas)]"
                        data-testid="chat-messages"
                    >
                        {messages.map((m, idx) => {
                            const isLastAssistant = idx === messages.length - 1 && m.role === "assistant";
                            return (
                                <MessageBubble
                                    key={idx}
                                    role={m.role}
                                    content={m.content}
                                    isStreaming={isStreaming && isLastAssistant}
                                    pose={isLastAssistant ? pose : "idle"}
                                />
                            );
                        })}
                        {error && (
                            <div
                                className="rounded-lg border border-[color:var(--danger)]/30 bg-white p-2 flex items-start gap-2"
                                data-testid="chat-error"
                            >
                                <span className="text-xs text-[color:var(--danger)] flex-1">
                                    {error}
                                </span>
                                {lastUserMessage && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setError("");
                                            sendMessage(lastUserMessage);
                                        }}
                                        className="inline-flex items-center gap-1 rounded-md bg-[color:var(--brand-teal-2)] text-white text-[11px] font-semibold px-2 py-1 hover:bg-[color:var(--brand-teal)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-teal-2)]"
                                        data-testid="chat-retry"
                                    >
                                        <RefreshCcw className="h-3 w-3" />
                                        {t("cta.tryAgain")}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Suggestions (only when we haven't chatted yet) */}
                    {messages.length === 1 && !isStreaming && (
                        <div className="px-4 pb-2">
                            <p className="text-[11px] uppercase tracking-wider text-[color:var(--text-soft)] mb-1.5">
                                {t("chat.suggestedQuestions")}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {SUGGESTIONS.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => sendMessage(s)}
                                        className="rounded-full border border-[color:var(--border-default)] bg-white px-2.5 py-1 text-[11px] text-[color:var(--brand-navy)] hover:bg-[color:var(--brand-teal-soft)] hover:border-[color:var(--brand-teal-2)]/30"
                                        data-testid="chat-suggestion"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Composer */}
                    <form
                        className="border-t border-[color:var(--border-default)] p-3 bg-white flex flex-col gap-2"
                        onSubmit={(e) => {
                            e.preventDefault();
                            sendMessage();
                        }}
                    >
                        <div className="flex items-end gap-2">
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKeyDownInput}
                                placeholder={t("chat.placeholder")}
                                rows={1}
                                maxLength={1500}
                                data-testid="chat-input"
                                className="flex-1 resize-none rounded-xl border border-[color:var(--border-default)] bg-white px-3 py-2 text-sm text-[color:var(--text-primary)] focus:outline-none focus:border-[color:var(--brand-teal-2)]"
                                style={{ maxHeight: 100 }}
                            />
                            <button
                                type="submit"
                                disabled={isStreaming || !input.trim()}
                                data-testid="chat-send"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-navy)] text-white disabled:opacity-40"
                                aria-label={t("chat.send")}
                            >
                                <SendHorizontal className="h-4 w-4" />
                            </button>
                        </div>
                        <p className="flex items-center gap-1.5 text-[10.5px] text-[color:var(--text-soft)] leading-snug">
                            <ShieldCheck className="h-3 w-3 text-[color:var(--brand-teal-2)]" />
                            {t("chat.disclaimer")}
                        </p>
                    </form>
                </div>
            )}

            {/* Local keyframes */}
            <style>{`
                @keyframes chatPulseRing {
                    0%   { box-shadow: 0 0 0 0 rgba(14,124,134,0.55); }
                    70%  { box-shadow: 0 0 0 18px rgba(14,124,134,0);   }
                    100% { box-shadow: 0 0 0 0 rgba(14,124,134,0);     }
                }
                @keyframes chatHalo {
                    0%   { transform: scale(1);    opacity: 0.75; }
                    70%  { transform: scale(1.35); opacity: 0;    }
                    100% { transform: scale(1.35); opacity: 0;    }
                }
                @keyframes chatBob {
                    0%, 100% { transform: translateY(0); }
                    50%      { transform: translateY(-3px); }
                }
                @keyframes chatBreath {
                    0%, 100% { transform: scale(1) rotate(0deg); }
                    50%      { transform: scale(1.015) rotate(0deg); }
                }
                @keyframes chatSway {
                    0%   { transform: rotate(0deg); }
                    25%  { transform: rotate(-1.4deg); }
                    50%  { transform: rotate(0deg); }
                    75%  { transform: rotate(1.4deg); }
                    100% { transform: rotate(0deg); }
                }
                /* Combine breath + sway using two properties on the same wrapper via composite */
                @keyframes chatBlink {
                    0%, 45%, 47%, 100% { filter: none; }
                    46%                 { filter: brightness(0.85) contrast(1.05); }
                }
                @keyframes chatPanelIn {
                    from { opacity: 0; transform: translateY(10px) scale(0.98); }
                    to   { opacity: 1; transform: translateY(0)    scale(1);    }
                }
                @keyframes statusBlink {
                    0%, 100% { opacity: 1; }
                    50%      { opacity: 0.35; }
                }
                @keyframes greetingIn {
                    from { opacity: 0; transform: translateY(6px) scale(0.9); }
                    to   { opacity: 1; transform: translateY(0)    scale(1);   }
                }
                @media (prefers-reduced-motion: reduce) {
                    [data-testid="chat-avatar-trigger"] * {
                        animation: none !important;
                    }
                }
            `}</style>
        </>
    );
}

function MessageBubble({ role, content, isStreaming, pose = "idle" }) {
    const isUser = role === "user";
    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"} gap-2`}>
            {!isUser && (
                <span className="mt-0.5">
                    <CharacterAvatar pose={pose} size="sm" />
                </span>
            )}
            <div
                className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    isUser
                        ? "bg-[color:var(--brand-navy)] text-white rounded-br-sm"
                        : "bg-white border border-[color:var(--border-default)] text-[color:var(--text-primary)] rounded-bl-sm"
                }`}
            >
                {content || (isStreaming ? <TypingDots /> : null)}
                {isStreaming && content ? <BlinkingCursor /> : null}
            </div>
        </div>
    );
}

function TypingDots() {
    const { t } = useTranslation();
    return (
        <span className="inline-flex items-center gap-1" aria-label={t("chat.typingLabel")}>
            <Dot delay="0s" />
            <Dot delay="0.15s" />
            <Dot delay="0.3s" />
            <style>{`
                @keyframes dotBlink {
                    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
                    40%           { opacity: 1;    transform: translateY(-2px); }
                }
            `}</style>
        </span>
    );
}

function Dot({ delay }) {
    return (
        <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{
                background: "var(--brand-teal)",
                animation: `dotBlink 1.1s infinite`,
                animationDelay: delay,
            }}
        />
    );
}

function BlinkingCursor() {
    return (
        <span
            aria-hidden
            className="inline-block ml-0.5 align-baseline"
            style={{
                width: "2px",
                height: "1em",
                background: "currentColor",
                opacity: 0.6,
                animation: "blink 1s steps(2, start) infinite",
            }}
        >
            <style>{`@keyframes blink { 50% { opacity: 0; } }`}</style>
        </span>
    );
}
